import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, CalendarRange, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fetchWorkbook } from '@/components/planning/planningApi';
import { PlannerYearTable } from '@/components/planning/PlannerYearTable';
import { PlannerSchemeCards } from '@/components/planning/PlannerSchemeCards';
import { PlannerLessons } from '@/components/planning/PlannerLessons';
import { PlannerProgress } from '@/components/planning/PlannerProgress';
import { PlannerSections } from '@/components/planning/PlannerSections';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlanningWorkbookPage() {
  const [params, setParams] = useSearchParams();
  const subject = params.get('subject');
  const year = params.get('year');
  const tab = params.get('tab') || 'lessons';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [focusWeek, setFocusWeek] = useState(null);

  useEffect(() => {
    if (!subject || !year) return;
    fetchWorkbook(subject, year).then(setData).catch(() => setError('No planning workbook has been uploaded for this subject yet.'));
  }, [subject, year]);

  const setTab = (t) => { params.set('tab', t); setParams(params, { replace: true }); };
  const goToLessons = (w) => { setFocusWeek({ term: w.term, week: w.week }); setTab('lessons'); };

  const onStatusChange = (lessonId, status, note) => {
    setData((d) => {
      const lessons = d.lessons.map((l) => (l.id === lessonId ? { ...l, status, note } : l));
      return { ...d, lessons, progress: recompute(lessons) };
    });
  };

  if (error) return <div className="p-8 text-center text-slate-500" data-testid="planner-missing">{error} <Link className="text-emerald-700 underline" to="/dashboard/resource-library">Back to Resource Library</Link></div>;
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  const { workbook, weeks, lessons, progress, today, can_tick: canTick } = data;
  const token = localStorage.getItem('authToken');

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="planner-page">
      <div className="rounded-2xl bg-gradient-to-br from-sky-700 via-indigo-700 to-violet-700 text-white p-6 shadow-lg flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4" data-testid="planner-hero">
        <div>
          <Link to="/dashboard/resource-library" className="text-xs text-sky-100 hover:text-white inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" />Resource Library</Link>
          <h1 className="text-2xl font-bold flex items-center gap-2 mt-1"><CalendarRange className="h-6 w-6 text-cyan-200" />{workbook.title}</h1>
          {workbook.school_line && <p className="text-sm text-sky-100/90">{workbook.school_line}</p>}
        </div>
        <div className="flex items-center gap-4">
          <div className="min-w-[220px]">
            <div className="flex justify-between text-xs text-sky-100 mb-1"><span>Taught this year</span><span className="font-semibold text-white" data-testid="planner-header-percent">{progress.overall.percent}%</span></div>
            <div className="h-2.5 rounded-full bg-white/20 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-cyan-300 to-emerald-300 transition-all" style={{ width: `${progress.overall.percent}%` }} /></div>
          </div>
          {workbook.resource_id && (
            <Button asChild size="sm" className="bg-white/15 hover:bg-white/25 text-white border border-white/30" data-testid="planner-download-excel">
              <a href={`${API}/api/resource-library/files/${workbook.resource_id}?token=${token}&download=1`}><Download className="h-4 w-4 mr-1" />Excel</a>
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto bg-slate-100 p-1">
          <TabsTrigger value="year" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white" data-testid="planner-tab-year">1 · Year Planner</TabsTrigger>
          <TabsTrigger value="scheme" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="planner-tab-scheme">2 · Scheme of Work</TabsTrigger>
          <TabsTrigger value="lessons" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white" data-testid="planner-tab-lessons">3 · Daily Lessons</TabsTrigger>
          {workbook.strategies && <TabsTrigger value="strategies" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="planner-tab-strategies">4 · Teaching Strategies</TabsTrigger>}
          {workbook.assessment_plan && <TabsTrigger value="assessment" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white" data-testid="planner-tab-assessment">5 · Assessment Plan</TabsTrigger>}
          <TabsTrigger value="progress" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white" data-testid="planner-tab-progress">Progress</TabsTrigger>
        </TabsList>
        <TabsContent value="year" className="mt-4"><PlannerYearTable weeks={weeks} today={today} onOpenWeek={goToLessons} /></TabsContent>
        <TabsContent value="scheme" className="mt-4"><PlannerSchemeCards weeks={weeks} lessons={lessons} today={today} onGoToLessons={goToLessons} /></TabsContent>
        <TabsContent value="lessons" className="mt-4"><PlannerLessons lessons={lessons} today={today} canTick={canTick} onChange={onStatusChange} focusWeek={focusWeek} isPrimary={Number(workbook.year_level) <= 6} title={workbook.title} /></TabsContent>
        <TabsContent value="strategies" className="mt-4"><PlannerSections doc={workbook.strategies} testId="planner-strategies" /></TabsContent>
        <TabsContent value="assessment" className="mt-4"><PlannerSections doc={workbook.assessment_plan} testId="planner-assessment" /></TabsContent>
        <TabsContent value="progress" className="mt-4"><PlannerProgress progress={progress} /></TabsContent>
      </Tabs>
    </div>
  );
}

function recompute(lessons) {
  const bucket = (items) => {
    const taught = items.filter((l) => l.status === 'taught').length;
    const carried = items.filter((l) => l.status === 'carried_over').length;
    const n = items.length;
    return { planned: n, taught, carried_over: carried, not_yet: n - taught - carried, percent: n ? Math.round((100 * taught) / n) : 0 };
  };
  const group = (key, fallback) => Object.entries(lessons.reduce((acc, l) => { const k = l[key] || fallback; (acc[k] = acc[k] || []).push(l); return acc; }, {}));
  return {
    overall: bucket(lessons),
    by_term: group('term').map(([term, v]) => ({ term, ...bucket(v) })),
    by_chapter: group('chapter', 'General').map(([chapter, v]) => ({ chapter, ...bucket(v) })),
  };
}
